import { useState, useEffect, useRef, useCallback } from "react";
import JsBarcode from "jsbarcode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, Search, Minus, Plus, AlertTriangle, SlidersHorizontal } from "lucide-react";
import {
  DEFAULT_BARCODE_CALIBRATION,
  type BarcodeCalibration,
  detectBarcodeFormat,
  getBarcodeCalibrationStorageKey,
  getBarcodeDensityColor,
  getBarcodeFormatLabel,
  getQuietZoneMargins,
  loadBarcodeCalibration,
  saveBarcodeCalibration,
} from "@/lib/barcode-utils";
import { defaultLabelCopies, MAX_LABEL_COPIES } from "@/lib/variant-labels";

interface Product {
  id: number;
  selectionId?: string;
  name: string;
  variantLabel?: string;
  sku: string;
  price: string | number;
  stock?: number;
  categoryName?: string | null;
}

const productKey = (product: Product) => product.selectionId ?? `product:${product.id}`;

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, char => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  })[char]!);
}

interface LabelConfig {
  size: "small" | "medium" | "large";
  showPrice: boolean;
  showSku: boolean;
  showName: boolean;
  storeName: string;
}

const SIZE_CONFIG = {
  small:  { label: "Small (38×25mm)",  widthMm: 38, heightMm: 25, widthPx: 144, heightPx: 96,  barcodeWidth: 1.2, barcodeHeight: 36, fontSize: 6.5 },
  medium: { label: "Medium (57×32mm)", widthMm: 57, heightMm: 32, widthPx: 216, heightPx: 121, barcodeWidth: 1.5, barcodeHeight: 50, fontSize: 8 },
  large:  { label: "Large (90×50mm)",  widthMm: 90, heightMm: 50, widthPx: 340, heightPx: 189, barcodeWidth: 2, barcodeHeight: 72, fontSize: 10 },
};

function BarcodeLabel({ product, qty, config, calibration }: { product: Product; qty: number; config: LabelConfig; calibration: BarcodeCalibration }) {
  const sz = SIZE_CONFIG[config.size];

  return (
    <>
      {Array.from({ length: qty }).map((_, i) => (
        <div
          key={`${productKey(product)}-${i}`}
          className="barcode-label"
          style={{
            width: sz.widthPx,
            height: sz.heightPx,
            border: "1px solid #ccc",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "4px 6px",
            boxSizing: "border-box",
            gap: 1,
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, maxWidth: "100%", transform: `translate(${calibration.horizontalOffsetMm}mm, ${calibration.verticalOffsetMm}mm)` }}>
            {config.showName && (
              <p style={{ fontSize: sz.fontSize, fontWeight: 700, lineHeight: 1.1, textAlign: "center", margin: 0, maxWidth: "100%", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                {product.name}
              </p>
            )}
            {product.variantLabel && (
              <p style={{ fontSize: sz.fontSize, fontWeight: 700, lineHeight: 1.1, textAlign: "center", margin: 0, maxWidth: "100%", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                {product.variantLabel}
              </p>
            )}
            {config.storeName && (
              <p style={{ fontSize: sz.fontSize, fontWeight: 600, color: "#000", margin: 0, lineHeight: 1, maxWidth: "100%", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{config.storeName}</p>
            )}
            {product.sku.trim() && <BarcodeImage sku={product.sku} barcodeWidth={sz.barcodeWidth} barcodeHeight={sz.barcodeHeight} fontSize={sz.fontSize} density={calibration.density} />}
            {config.showSku && product.sku.trim() && (
              <p style={{ fontSize: sz.fontSize, fontWeight: 600, color: "#000", margin: 0, fontFamily: "monospace", lineHeight: 1, maxWidth: "100%", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{product.sku}</p>
            )}
            {config.showPrice && (
              <p style={{ fontSize: sz.fontSize + 1, fontWeight: 800, margin: 0, lineHeight: 1 }}>
                ₦{Number(product.price).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
              </p>
            )}
          </div>
        </div>
      ))}
    </>
  );
}

function BarcodeImage({ sku, barcodeWidth, barcodeHeight, fontSize, density }: { sku: string; barcodeWidth: number; barcodeHeight: number; fontSize: number; density: BarcodeCalibration["density"] }) {
  const ref = useRef<SVGSVGElement>(null);
  const format = detectBarcodeFormat(sku);

  useEffect(() => {
    if (!ref.current) return;
    try {
      const quietZone = getQuietZoneMargins(format, barcodeWidth);
      JsBarcode(ref.current, sku, {
        format,
        width: barcodeWidth,
        height: barcodeHeight,
        displayValue: false,
        marginTop: 1,
        marginBottom: 1,
        lineColor: getBarcodeDensityColor(density),
        ...quietZone,
      });
    } catch {
      // Invalid barcode value — skip silently
    }
  }, [sku, barcodeWidth, barcodeHeight, fontSize, format, density]);

  return <svg ref={ref} role="img" aria-label={`${getBarcodeFormatLabel(format)} barcode ${sku}`} style={{ maxWidth: "100%", height: "auto" }} />;
}

interface BarcodePrinterProps {
  open: boolean;
  onClose: () => void;
  products: Product[];
  unprintableNames?: string[];
  storeId: string | number;
  storeName?: string;
  autoSelectAll?: boolean;
}

type BarcodeConflict = {
  sku: string;
  items: { productId: number; productName: string; variantLabel?: string }[];
};

export function BarcodePrinter({ open, onClose, products, unprintableNames = [], storeId, storeName = "", autoSelectAll = false }: BarcodePrinterProps) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Record<string, number>>({});
  const [config, setConfig] = useState<LabelConfig>({
    size: "medium",
    showPrice: true,
    showSku: true,
    showName: true,
    storeName,
  });
  const [previewing, setPreviewing] = useState(false);
  const [showCalibration, setShowCalibration] = useState(false);
  const [calibration, setCalibration] = useState<BarcodeCalibration>(DEFAULT_BARCODE_CALIBRATION);
  const [calibrationSaved, setCalibrationSaved] = useState(false);
  const [barcodeConflicts, setBarcodeConflicts] = useState<BarcodeConflict[]>([]);
  const [conflictStatus, setConflictStatus] = useState<"loading" | "ready" | "error">("loading");

  const calibrationStorageKey = getBarcodeCalibrationStorageKey(storeId, config.size);

  useEffect(() => {
    setCalibration(loadBarcodeCalibration(window.localStorage, storeId, config.size));
    setCalibrationSaved(false);
  }, [calibrationStorageKey, storeId, config.size]);

  useEffect(() => {
    if (!open || !autoSelectAll) return;
    const next: Record<string, number> = {};
    products.forEach(product => {
      const copies = defaultLabelCopies(product);
      if (copies > 0) next[productKey(product)] = copies;
    });
    setSelected(next);
  }, [open, autoSelectAll, products]);

  useEffect(() => {
    if (!open) {
      setSelected({});
      setSearch("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setConflictStatus("loading");
    setBarcodeConflicts([]);
    fetch(`${import.meta.env.BASE_URL.replace(/\/$/, "")}/api/products/barcode-conflicts`, {
      credentials: "include", signal: controller.signal,
    }).then(async response => {
      if (!response.ok) throw new Error("Could not check saved barcodes");
      return response.json() as Promise<{ conflicts: BarcodeConflict[] }>;
    }).then(data => {
      if (controller.signal.aborted) return;
      setBarcodeConflicts(data.conflicts);
      setConflictStatus("ready");
    }).catch(() => {
      if (!controller.signal.aborted) setConflictStatus("error");
    });
    return () => controller.abort();
  }, [open, storeId]);

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.variantLabel?.toLowerCase().includes(search.toLowerCase()) ?? false) ||
    p.sku.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (product: Product) => {
    const id = productKey(product);
    setSelected(prev => {
      if (prev[id]) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: defaultLabelCopies(product) || 1 };
    });
  };

  const setQty = (id: string, qty: number) => {
    if (qty <= 0) {
      setSelected(prev => { const next = { ...prev }; delete next[id]; return next; });
    } else {
      setSelected(prev => ({ ...prev, [id]: Math.min(qty, MAX_LABEL_COPIES) }));
    }
  };

  const selectAll = () => {
    const next: Record<string, number> = {};
    filtered.forEach(product => {
      const key = productKey(product);
      const copies = selected[key] ?? defaultLabelCopies(product);
      if (copies > 0) next[key] = copies;
    });
    setSelected(next);
  };

  const clearAll = () => setSelected({});

  const selectedProducts = products.filter(p => selected[productKey(p)]);
  const selectedCount = selectedProducts.reduce((sum, product) => sum + selected[productKey(product)], 0);
  const selectedConflicts = barcodeConflicts.filter(conflict =>
    selectedProducts.some(product => product.sku.trim().toLowerCase() === conflict.sku.trim().toLowerCase())
  );
  const priceOnlyCount = selectedProducts.filter(p => !p.sku.trim()).length;
  const hasRetailBarcode = selectedProducts.some(p => p.sku.trim() && detectBarcodeFormat(p.sku) !== "CODE128");
  const hasLongInternalCode = selectedProducts.some(p => {
    if (!p.sku.trim()) return false;
    if (detectBarcodeFormat(p.sku) !== "CODE128") return false;
    const recommendedMaximum = config.size === "small" ? 16 : config.size === "medium" ? 28 : 45;
    return p.sku.trim().length > recommendedMaximum;
  });

  const handlePrint = useCallback(() => {
    if (conflictStatus !== "ready" || selectedConflicts.length > 0) return;
    const sz = SIZE_CONFIG[config.size];
    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) return;

    // Build label HTML for each selected product × qty
    const labelsHtml = selectedProducts.flatMap(p =>
      Array.from({ length: selected[productKey(p)] }, () => {
        // Generate barcode SVG via canvas trick
        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        if (p.sku.trim()) try {
          const format = detectBarcodeFormat(p.sku);
          const quietZone = getQuietZoneMargins(format, sz.barcodeWidth);
          JsBarcode(svg, p.sku, {
            format,
            width: sz.barcodeWidth,
            height: sz.barcodeHeight,
            displayValue: false,
            marginTop: 1,
            marginBottom: 1,
            lineColor: getBarcodeDensityColor(calibration.density),
            ...quietZone,
          });
        } catch {}
        const svgStr = new XMLSerializer().serializeToString(svg);
        const svgB64 = btoa(unescape(encodeURIComponent(svgStr)));

        return `<div class="label"><div class="label-content">
          ${config.showName ? `<p class="name">${escapeHtml(p.name)}</p>` : ""}
          ${p.variantLabel ? `<p class="variant">${escapeHtml(p.variantLabel)}</p>` : ""}
          ${config.storeName ? `<p class="store">${escapeHtml(config.storeName)}</p>` : ""}
          ${p.sku.trim() ? `<img src="data:image/svg+xml;base64,${svgB64}" class="barcode" alt="" />` : ""}
          ${config.showSku && p.sku.trim() ? `<p class="sku">${escapeHtml(p.sku)}</p>` : ""}
          ${config.showPrice ? `<p class="price">₦${Number(p.price).toLocaleString("en-NG", { minimumFractionDigits: 2 })}</p>` : ""}
        </div>
        </div>`;
      })
    ).join("");

    printWindow.document.write(`<!DOCTYPE html><html><head><title>Barcode Labels</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; background: white; }
  .sheet { display: flex; flex-wrap: wrap; gap: ${calibration.gapMm}mm; }
  .label {
    width: ${sz.widthMm}mm;
    height: ${sz.heightMm}mm;
    border: 1px solid #bbb;
    overflow: hidden;
    page-break-inside: avoid;
  }
  .label-content {
    width: 100%; height: 100%; padding: 4px 6px;
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px;
    transform: translate(${calibration.horizontalOffsetMm}mm, ${calibration.verticalOffsetMm}mm);
  }
  .name { font-size: ${sz.fontSize}pt; font-weight: 700; text-align: center; max-width: 100%; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
  .variant { font-size: ${sz.fontSize}pt; font-weight: 700; text-align: center; max-width: 100%; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
   .store, .sku {
     font-size: ${sz.fontSize}pt;
     font-weight: 600;
     color: #000;
     max-width: 100%;
     overflow: hidden;
     white-space: nowrap;
     text-overflow: ellipsis;
   }
   .sku { font-family: monospace; }
  .price { font-size: ${sz.fontSize + 1}pt; font-weight: 800; }
  .barcode { display: block; margin: 0 auto; max-width: 100%; height: auto; }
  @page { size: auto; margin: 0; }
  @media print {
    body { margin: 0; }
    .sheet { gap: ${calibration.gapMm}mm; }
    .label { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .store, .sku { color: #000 !important; }
  }
</style></head><body>
<div class="sheet">${labelsHtml}</div>
<script>window.onload = () => { window.print(); }<\/script>
</body></html>`);
    printWindow.document.close();
  }, [selectedProducts, selected, config, calibration, conflictStatus, selectedConflicts]);

  const saveCalibration = () => {
    const normalized = saveBarcodeCalibration(window.localStorage, storeId, config.size, calibration);
    setCalibration(normalized);
    setCalibrationSaved(true);
  };

  const handlePrintCalibration = useCallback(() => {
    const sz = SIZE_CONFIG[config.size];
    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) return;
    const densityColor = getBarcodeDensityColor(calibration.density);
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Barcode Printer Calibration — ${sz.label}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; color: #111; background: white; }
  .instructions { max-width: 190mm; padding: 8mm; font-size: 11pt; line-height: 1.45; }
  .instructions h1 { font-size: 16pt; margin-bottom: 2mm; }
  .instructions strong { font-size: 13pt; }
  .sheet { display: flex; flex-wrap: wrap; gap: ${calibration.gapMm}mm; }
  .label { width: ${sz.widthMm}mm; height: ${sz.heightMm}mm; border: .3mm solid #111; overflow: hidden; page-break-inside: avoid; position: relative; }
  .label:before, .label:after { content: ""; position: absolute; background: #aaa; }
  .label:before { width: 100%; height: .2mm; top: 50%; left: 0; }
  .label:after { width: .2mm; height: 100%; left: 50%; top: 0; }
  .content { position: relative; z-index: 1; width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; transform: translate(${calibration.horizontalOffsetMm}mm, ${calibration.verticalOffsetMm}mm); }
  .target { width: 8mm; height: 8mm; border: .5mm solid ${densityColor}; border-radius: 50%; position: relative; background: white; }
  .target:before, .target:after { content: ""; position: absolute; background: ${densityColor}; }
  .target:before { width: 12mm; height: .4mm; left: -2.5mm; top: 3.3mm; }
  .target:after { height: 12mm; width: .4mm; top: -2.5mm; left: 3.3mm; }
  .caption { margin-top: 2mm; padding: .5mm 1mm; font-size: ${Math.max(5, sz.fontSize - 1)}pt; font-weight: bold; background: white; text-align: center; }
  @page { size: auto; margin: 0; }
  @media print { .instructions { display: none; } .label { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
</style></head><body>
<div class="instructions">
  <h1>Printer calibration: ${sz.label}</h1>
  <p><strong>In the print dialog, choose 100% or Actual Size.</strong> Turn off “Fit to page”, headers, and footers.</p>
  <p>The outer border should follow each label edge and the target should sit in the centre. Measure any shift, enter the correction in RetailPulse, save it, and print this sheet again.</p>
</div>
<div class="sheet">${Array.from({ length: 6 }, (_, index) => `<div class="label"><div class="content"><div class="target"></div><div class="caption">${index + 1} · ${sz.widthMm}×${sz.heightMm} mm · ${calibration.density}</div></div></div>`).join("")}</div>
<script>window.onload = () => window.print();<\/script></body></html>`);
    printWindow.document.close();
  }, [config.size, calibration]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-4xl h-[calc(100dvh-1rem)] sm:h-auto sm:max-h-[90dvh] flex flex-col gap-0 p-0 overflow-hidden">
        <DialogHeader className="px-4 sm:px-6 pt-5 pb-4 pr-12 border-b flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Printer className="w-5 h-5" /> Print Barcode / Price Labels
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col sm:flex-row flex-1 min-h-0 overflow-hidden">
          {/* Left — product list */}
          <div className="w-full sm:w-80 h-[26dvh] sm:h-auto flex-shrink-0 border-b sm:border-b-0 sm:border-r flex flex-col min-h-0">
            <div className="p-3 sm:p-4 border-b space-y-3">
              {unprintableNames.length > 0 && (
                <p className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive" data-testid="status-unprintable-variants">
                  No labels for {unprintableNames.slice(0, 2).join(", ")}{unprintableNames.length > 2 ? ` and ${unprintableNames.length - 2} more` : ""}: variant rows are missing or invalid. Edit the product to correct them.
                </p>
              )}
              {barcodeConflicts.length > 0 && (
                <p className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                  {barcodeConflicts.length} shared barcode{barcodeConflicts.length === 1 ? "" : "s"} found in this store. Edit the affected products or variants to give each a unique SKU.
                </p>
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input data-testid="input-search-label-items" placeholder="Search products, variants or SKUs..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-8 text-sm" />
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <button onClick={selectAll} className="text-primary hover:underline">Select all</button>
                <span>{selectedCount} label{selectedCount !== 1 ? "s" : ""} selected</span>
                <button onClick={clearAll} className="hover:underline">Clear</button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Variant counts start at whole units in stock, up to 9,999. Zero-stock variants stay unselected; other products start at 1.
              </p>
            </div>

            <div className="flex-1 overflow-y-auto">
              {filtered.map(p => {
                const key = productKey(p);
                const qty = selected[key] ?? 0;
                const isSelected = qty > 0;
                return (
                  <div
                    key={key}
                    className={`flex items-center gap-3 px-4 py-2.5 border-b last:border-b-0 transition-colors ${isSelected ? "bg-primary/5" : "hover:bg-muted/40"}`}
                  >
                    <Checkbox data-testid={`checkbox-label-${key}`} aria-label={`Select labels for ${p.name}${p.variantLabel ? `, ${p.variantLabel}` : ""}`} checked={isSelected} onCheckedChange={() => toggle(p)} />
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => toggle(p)} role="button" tabIndex={0} onKeyDown={event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); toggle(p); } }}>
                      <p className="text-sm font-medium truncate" data-testid={`text-label-name-${key}`}>{p.name}{p.variantLabel && ` — ${p.variantLabel}`}</p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {p.sku || "Price-only · add a variant SKU for a barcode"}
                        {p.stock != null && ` · ${p.stock} in stock`}
                      </p>
                    </div>
                    {isSelected && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button data-testid={`button-decrease-labels-${key}`} aria-label={`Fewer labels for ${p.name}`} onClick={() => setQty(key, qty - 1)} className="w-5 h-5 rounded border flex items-center justify-center hover:bg-muted">
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="text-xs font-bold w-5 text-center">{qty}</span>
                        <button data-testid={`button-increase-labels-${key}`} aria-label={`More labels for ${p.name}`} onClick={() => setQty(key, qty + 1)} className="w-5 h-5 rounded border flex items-center justify-center hover:bg-muted">
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right — settings + preview */}
          <div className="flex-1 flex flex-col min-w-0 min-h-0">
            {/* Settings bar */}
            <div className="p-3 sm:p-4 border-b flex flex-wrap items-center gap-3 sm:gap-4 flex-shrink-0 max-h-[36dvh] sm:max-h-none overflow-y-auto">
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-muted-foreground whitespace-nowrap">Label size</label>
                <Select value={config.size} onValueChange={v => setConfig(c => ({ ...c, size: v as LabelConfig["size"] }))}>
                  <SelectTrigger className="h-7 text-xs w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SIZE_CONFIG).map(([k, v]) => (
                      <SelectItem key={k} value={k} className="text-xs">{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="w-full text-[11px] text-muted-foreground">
                Valid EAN-13, EAN-8 and UPC-A numbers keep their retail format. Other item codes print as Code 128. Print at 100% or Actual Size.
              </p>

              <div className="w-full rounded-md border bg-muted/20">
                <button type="button" onClick={() => setShowCalibration(value => !value)} className="flex w-full items-center justify-between px-3 py-2 text-xs font-medium">
                  <span className="flex items-center gap-2"><SlidersHorizontal className="h-3.5 w-3.5" /> Printer calibration</span>
                  <span className="text-muted-foreground">{showCalibration ? "Hide" : "Adjust"}</span>
                </button>
                {showCalibration && (
                  <div className="grid grid-cols-2 gap-3 border-t p-3">
                    {[
                      { key: "horizontalOffsetMm", label: "Horizontal offset (mm)" },
                      { key: "verticalOffsetMm", label: "Vertical offset (mm)" },
                      { key: "gapMm", label: "Label gap (mm)" },
                    ].map(field => (
                      <label key={field.key} className="space-y-1 text-[11px] text-muted-foreground">
                        <span>{field.label}</span>
                        <Input
                          type="number"
                          step="0.1"
                          min={field.key === "gapMm" ? 0 : -10}
                          max={field.key === "gapMm" ? 20 : 10}
                          value={calibration[field.key as keyof BarcodeCalibration] as number}
                          onChange={event => {
                            setCalibrationSaved(false);
                            setCalibration(current => ({ ...current, [field.key]: event.target.valueAsNumber }));
                          }}
                          className="h-8 text-xs"
                        />
                      </label>
                    ))}
                    <label className="space-y-1 text-[11px] text-muted-foreground">
                      <span>Print density</span>
                      <Select value={calibration.density} onValueChange={density => { setCalibrationSaved(false); setCalibration(current => ({ ...current, density: density as BarcodeCalibration["density"] })); }}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="light">Light</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="dark">Dark</SelectItem>
                        </SelectContent>
                      </Select>
                    </label>
                    <p className="col-span-2 text-[11px] text-muted-foreground">Positive horizontal values move content right; positive vertical values move it down. Settings are saved for this store and label size.</p>
                    <div className="col-span-2 flex flex-wrap items-center gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={handlePrintCalibration}><Printer className="mr-1.5 h-3.5 w-3.5" /> Print calibration sheet</Button>
                      <Button type="button" size="sm" onClick={saveCalibration}>Save calibration</Button>
                      {calibrationSaved && <span className="text-[11px] font-medium text-emerald-700">Saved</span>}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3 text-xs">
                {[
                  { key: "showName", label: "Name" },
                  { key: "showPrice", label: "Price" },
                  { key: "showSku", label: "SKU" },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-1.5 cursor-pointer">
                    <Checkbox
                      checked={config[key as keyof LabelConfig] as boolean}
                      onCheckedChange={v => setConfig(c => ({ ...c, [key]: !!v }))}
                    />
                    {label}
                  </label>
                ))}
              </div>

              <div className="flex items-center gap-2 ml-auto">
                <Input
                  placeholder="Store name on label"
                  value={config.storeName}
                  onChange={e => setConfig(c => ({ ...c, storeName: e.target.value }))}
                  className="h-7 text-xs w-40"
                />
              </div>
            </div>

            {/* Preview */}
            <div className="flex-1 min-h-0 overflow-auto bg-muted/30 p-3 sm:p-4">
              {selectedProducts.length === 0 ? (
                <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                  <div className="text-center space-y-2">
                    <Printer className="w-10 h-10 mx-auto opacity-20" />
                    <p>{products.length ? "Select products or variants on the left to preview labels" : "No printable products or variants found."}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground px-1">{selectedCount} label{selectedCount !== 1 ? "s" : ""} — preview</p>
                   {((config.size === "small" && hasRetailBarcode) || hasLongInternalCode) && (
                     <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                       <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                       <span>
                         {config.size === "small" && hasRetailBarcode
                           ? "Use Medium or Large for EAN/UPC labels so the required quiet zones are not compressed."
                           : "One or more item codes are long for this label size. Choose a larger label for more reliable scanning."}
                       </span>
                     </div>
                   )}
                   {priceOnlyCount > 0 && (
                     <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="status-price-only-labels">
                       {priceOnlyCount} selected item{priceOnlyCount === 1 ? "" : "s"} ha{priceOnlyCount === 1 ? "s" : "ve"} no SKU. These will print as price-only labels; add a unique variant SKU to print scannable barcodes.
                     </p>
                   )}
                    {selectedConflicts.length > 0 && (
                      <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive" data-testid="status-duplicate-barcodes">
                        <strong>Printing blocked: these barcodes identify multiple items.</strong>
                        {selectedConflicts.slice(0, 3).map(conflict => (
                          <p key={conflict.sku} className="mt-1">
                            {conflict.sku}: {conflict.items.map(item =>
                              `${item.productName}${item.variantLabel ? ` (${item.variantLabel})` : ""}`
                            ).join(" and ")}
                          </p>
                        ))}
                        <p className="mt-1">Close this window and edit each affected product in Inventory. Give every product and variant a unique SKU, then reopen labels.</p>
                      </div>
                    )}
                    {conflictStatus !== "ready" && (
                      <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="status-barcode-check">
                        {conflictStatus === "loading" ? "Checking barcodes before printing…" : "Could not check for duplicate barcodes. Close and reopen labels to retry; printing is paused."}
                      </p>
                    )}
                  <div className="flex flex-wrap bg-white rounded-lg p-3 border shadow-sm" style={{ gap: `${calibration.gapMm}mm` }}>
                    {selectedProducts.map(p => (
                       <BarcodeLabel key={productKey(p)} product={p} qty={selected[productKey(p)]} config={config} calibration={calibration} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-3 sm:px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t bg-background flex items-center justify-between gap-2 flex-shrink-0">
              <p className="hidden sm:block text-xs text-muted-foreground">
                {selectedCount} label{selectedCount !== 1 ? "s" : ""} across {selectedProducts.length} product{selectedProducts.length !== 1 ? "s" : ""}
                <span className="block mt-1">In the print dialog, choose the barcode/label printer.</span>
              </p>
              <div className="flex gap-2 w-full sm:w-auto">
                <Button variant="outline" size="sm" className="min-h-11 flex-1 sm:flex-none" onClick={onClose}>Cancel</Button>
                <Button
                  size="sm"
                  className="gap-2 min-h-11 flex-[2] sm:flex-none"
                  disabled={selectedCount === 0 || conflictStatus !== "ready" || selectedConflicts.length > 0}
                  onClick={handlePrint}
                >
                  <Printer className="w-4 h-4" /> Print {selectedCount > 0 ? `${selectedCount} Labels` : ""}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
